import { z } from "zod";
import { getUserTimeZoneId } from "../../../../utils/timeZoneUtils";
import {
    type CountryCode,
    isValidPhoneNumber,
    isPossiblePhoneNumber,
    parsePhoneNumberWithError,
} from "libphonenumber-js";

export enum Step {
    Initial = 0,
    Verification = 1,
    PasswordReset = 2,
}

function isUnusualAuNumber(value: string) {
    return (value.startsWith("05") && value.length === 10) || (value.startsWith("+615") && value.length === 12);
}

export const registerSchema = z
    .object({
        firstName: z.string().min(1, "First name is required"),
        lastName: z.string().min(1, "Last name is required"),
        email: z.string(),
        phoneNumber: z.string(),
        verificationCode: z.string(),
        timeZoneIdIana: z.string(),
        conditionsRequired: z.boolean(),
        conditions: z.boolean(),
        countryCode: z.string(),
        isSmsType: z.boolean(),
        step: z.nativeEnum(Step),
        password: z.string().optional(),
        passwordScore: z.number().optional(),
    })
    .superRefine((values, ctx) => {
        if (values.isSmsType) {
            if (values.phoneNumber) {
                if (!isValidPhoneNumber(values.phoneNumber, values.countryCode as CountryCode)) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: ["phoneNumber"],
                        message: `Not valid ${values.countryCode} number`,
                    });
                }
            } else {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["phoneNumber"],
                    message: "Mobile number is required",
                });
            }
        } else if (values.phoneNumber) {
            let isValid = true;
            if (values.countryCode === "AU" && isUnusualAuNumber(values.phoneNumber)) {
                isValid = true;
            } else {
                isValid = isPossiblePhoneNumber(values.phoneNumber, values.countryCode as CountryCode);
            }

            if (!isValid) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["phoneNumber"],
                    message: "Not a valid number",
                });
            }
        }

        if (!values.isSmsType) {
            if (!values.email || values.email.length < 1) {
                ctx.addIssue({
                    code: "too_small",
                    path: ["email"],
                    minimum: 1,
                    type: "string",
                    inclusive: true,
                    message: "Email is required",
                });
            }
        }

        // Validate email format if provided (for both SMS and email verification types)
        if (values.email && !z.string().email().safeParse(values.email).success) {
            ctx.addIssue({
                code: "invalid_string",
                path: ["email"],
                validation: "email",
                message: "Please enter a valid email address",
            });
        }

        if (values.step === Step.Verification) {
            if (!values.verificationCode) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["verificationCode"],
                    message: "Verification code is required",
                });
            } else if (values.verificationCode.length !== 6) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["verificationCode"],
                    message: "Verification code must be 6 characters long",
                });
            }

            if (values.conditionsRequired && !values.conditions) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["conditions"],
                    message: "You need to accept the terms and conditions",
                });
            }
        }

        if (values.step === Step.PasswordReset) {
            if (!values.password) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["password"],
                    message: "Password is required.",
                });
            }

            if (values.passwordScore < 2) {
                // to highlight with error color the password field
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["password"],
                    message: "",
                });
            }
        }
    });

export type RegisterType = z.infer<typeof registerSchema>;

export const generateDefaultValues = (countryCode: string, isSmsType: boolean): RegisterType => {
    return {
        firstName: "",
        lastName: "",
        email: "",
        password: "",
        phoneNumber: "",
        verificationCode: "",
        timeZoneIdIana: getUserTimeZoneId(),
        conditionsRequired: false,
        countryCode,
        isSmsType,
        conditions: false,
        step: Step.Initial,
    } as RegisterType;
};

function formatUnusualAuNumberAsNational(number: string): string {
    if (number.startsWith("+615")) {
        return number.replace("+615", "05");
    }

    return number;
}

export const formatPhoneNumber = (phoneNumber: string, countryCode: string): string => {
    if (countryCode === "AU") {
        const cleanPhoneNumber = phoneNumber.trim().replace(/\s/g, "");
        if (
            (cleanPhoneNumber.length === 10 && cleanPhoneNumber.startsWith("05")) ||
            (cleanPhoneNumber.length === 12 && cleanPhoneNumber.startsWith("+615"))
        ) {
            return formatUnusualAuNumberAsNational(cleanPhoneNumber);
        }
    }

    try {
        const parsedPhoneNumber = parsePhoneNumberWithError(phoneNumber, {
            defaultCountry: countryCode as CountryCode,
            extract: false,
        });
        return parsedPhoneNumber.formatNational();
    } catch {
        return phoneNumber; // Fallback to the original phone number if parsing fails
    }
};

export const getButtonTitle = (verificationStep: Step) => {
    switch (verificationStep) {
        case Step.Initial:
            return "Get Verification Code";
        case Step.Verification:
            return "Verify";
        case Step.PasswordReset:
            return "Reset Password";
    }
};
